// src/inventory/shared/inventoryShared.jsx
import React, { useState, useCallback } from 'react';

export const C = {
  primary:   '#1a56db',
  success:   '#0e9f6e',
  warning:   '#c27803',
  danger:    '#e02424',
  purple:    '#7e3af2',
  orange:    '#ff5a1f',
  teal:      '#0694a2',
  dark:      '#111928',
  bg:        '#f3f4f6',
  surface:   '#ffffff',
  border:    '#e5e7eb',
  text:      '#111928',
  textSoft:  '#6b7280',
  textMuted: '#9ca3af',
};

export const S = {
  page:        { padding: 0, fontFamily: "'IBM Plex Sans', system-ui, sans-serif" },
  card:        { background: C.surface, borderRadius: 12, border: `1px solid ${C.border}`, overflow: 'hidden' },
  modalOverlay:{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20, backdropFilter: 'blur(2px)' },
  modalBox:    { background: '#fff', borderRadius: 14, width: '100%', maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(0,0,0,0.2)' },
  modalHead:   { padding: '18px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 },
  modalFoot:   { padding: '14px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0, background: '#fafafa' },
  scrollBody:  { padding: 24, overflowY: 'auto', flex: 1 },
  btnPri:      { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: C.primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSec:      { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: '#fff', color: C.text, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  btnDng:      { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: '#fdf2f2', color: C.danger, border: `1px solid #f8d7d7`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSuc:      { display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: '#f3faf7', color: C.success, border: `1px solid #bcf0da`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnGhost:    { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 10px', background: 'transparent', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, color: C.textSoft },
  label:       { display: 'block', fontSize: 12, fontWeight: 600, color: C.textSoft, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' },
  input:       { width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14, outline: 'none', color: C.text, background: '#fff', boxSizing: 'border-box' },
  select:      { width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14, outline: 'none', color: C.text, background: '#fff', cursor: 'pointer', boxSizing: 'border-box' },
  textarea:    { width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14, outline: 'none', color: C.text, background: '#fff', boxSizing: 'border-box', minHeight: 80, resize: 'vertical', fontFamily: 'inherit' },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { padding: '11px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: C.textMuted, borderBottom: `1px solid ${C.border}`, background: '#f9fafb', textTransform: 'uppercase', letterSpacing: '0.07em' },
  td:          { padding: '12px 14px', borderBottom: `1px solid #f3f4f6`, fontSize: 13, color: C.text },
  center:      { padding: 48, textAlign: 'center', color: C.textMuted, fontSize: 14 },
  divider:     { height: 1, background: C.border, margin: '16px 0' },
  secTitle:    { fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 },
  errBox:      { background: '#fdf2f2', border: '1px solid #f8d7d7', borderRadius: 9, padding: '11px 14px', marginBottom: 14 },
};

export const Badge = ({ color = C.text, bg = '#f3f4f6', children, dot }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, color, background: bg }}>
    {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />}
    {children}
  </span>
);

export const StatusBadge = ({ status }) => {
  const m = {
    Active:     { color: '#065f46', bg: '#d1fae5' },
    Inactive:   { color: '#4b5563', bg: '#f3f4f6' },
    Pending:    { color: '#92400e', bg: '#fef3c7' },
    Approved:   { color: '#065f46', bg: '#d1fae5' },
    Rejected:   { color: '#991b1b', bg: '#fee2e2' },
    Received:   { color: '#1e40af', bg: '#dbeafe' },
    Completed:  { color: '#065f46', bg: '#d1fae5' },
    Cancelled:  { color: '#991b1b', bg: '#fee2e2' },
    Draft:      { color: '#4b5563', bg: '#f3f4f6' },
    'In Transit':{ color: '#1e40af', bg: '#dbeafe' },
    'Low Stock': { color: '#92400e', bg: '#fef3c7' },
    'Out of Stock':{ color:'#991b1b', bg:'#fee2e2' },
  };
  const s = m[status] || { color: '#374151', bg: '#f3f4f6' };
  return <Badge color={s.color} bg={s.bg} dot>{status}</Badge>;
};

export const FormField = ({ label, children, hint, col }) => (
  <div style={{ gridColumn: col ? `span ${col}` : undefined }}>
    <label style={S.label}>{label}</label>
    {children}
    {hint && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 3 }}>{hint}</div>}
  </div>
);

export const ErrList = ({ errors }) => !errors?.length ? null : (
  <div style={S.errBox}>
    {errors.map((e, i) => <div key={i} style={{ color: C.danger, fontSize: 13, display: 'flex', alignItems: 'center', gap: 7, marginBottom: i < errors.length-1 ? 3 : 0 }}>⚠ {e}</div>)}
  </div>
);

export const StatCard = ({ label, value, sub, color = C.primary, bg = '#eff6ff', Icon, trend }) => (
  <div style={{ background: bg, borderRadius: 12, padding: '16px 18px', border: `1px solid ${color}20` }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: C.textSoft, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      {Icon && <div style={{ width: 34, height: 34, borderRadius: 8, background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={17} color={color} /></div>}
    </div>
    <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 5 }}>{sub}</div>}
  </div>
);

export const PageHeader = ({ title, subtitle, Icon, color = C.primary, action }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
      {Icon && <div style={{ width: 44, height: 44, borderRadius: 11, background: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon size={21} color={color} /></div>}
      <div>
        <h1 style={{ fontSize: 21, fontWeight: 800, color: C.text, margin: 0, letterSpacing: '-0.02em' }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 13, color: C.textSoft, margin: '2px 0 0' }}>{subtitle}</p>}
      </div>
    </div>
    {action}
  </div>
);

export const FilterBar = ({ search, onSearch, filters = [], children }) => (
  <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
    <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
      <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: C.textMuted, fontSize: 14 }}>🔍</span>
      <input type="text" placeholder="Search..." value={search} onChange={e => onSearch(e.target.value)} style={{ ...S.input, paddingLeft: 34, fontSize: 13 }} />
    </div>
    {filters.map(f => (
      <select key={f.key} value={f.value} onChange={e => f.onChange(e.target.value)} style={{ ...S.select, width: 'auto', fontSize: 13, padding: '8px 12px' }}>
        {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    ))}
    {children}
  </div>
);

export const DataTable = ({ columns, data, loading, onRowClick, emptyTitle, page, totalPages, onPageChange, total }) => {
  if (loading) return <div style={S.center}><div style={{ width: 32, height: 32, border: `3px solid ${C.border}`, borderTop: `3px solid ${C.primary}`, borderRadius: '50%', animation: 'spin .8s linear infinite', margin: '0 auto 10px' }} /><style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>;
  if (!data?.length) return <div style={S.center}><div style={{ fontSize: 38, marginBottom: 10, opacity: 0.3 }}>📦</div><div style={{ fontWeight: 600, color: C.textSoft }}>{emptyTitle || 'No records found'}</div></div>;
  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <table style={S.table}>
          <thead><tr>{columns.map(c => <th key={c.key || c.label} style={S.th}>{c.label}</th>)}</tr></thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={row._id || row.id || i} style={{ cursor: onRowClick ? 'pointer' : 'default', transition: 'background .1s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                onClick={() => onRowClick?.(row)}>
                {columns.map(c => <td key={c.key || c.label} style={S.td}>{c.render ? c.render(row[c.key], row) : row[c.key] ?? '—'}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', borderTop: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 12, color: C.textMuted }}>{total} records</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={{ ...S.btnSec, padding: '6px 12px', fontSize: 12 }} disabled={page <= 1} onClick={() => onPageChange(page-1)}>Prev</button>
            <span style={{ padding: '6px 10px', fontSize: 12, color: C.textSoft }}>{page}/{totalPages}</span>
            <button style={{ ...S.btnSec, padding: '6px 12px', fontSize: 12 }} disabled={page >= totalPages} onClick={() => onPageChange(page+1)}>Next</button>
          </div>
        </div>
      )}
    </>
  );
};

export const Modal = ({ title, subtitle, onClose, children, footer, maxWidth = 620 }) => (
  <div style={S.modalOverlay} onClick={onClose}>
    <div style={{ ...S.modalBox, maxWidth }} onClick={e => e.stopPropagation()}>
      <div style={S.modalHead}>
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: C.text, margin: 0 }}>{title}</h2>
          {subtitle && <div style={{ fontSize: 12, color: C.textSoft, marginTop: 2 }}>{subtitle}</div>}
        </div>
        <button style={S.btnGhost} onClick={onClose}>✕</button>
      </div>
      <div style={S.scrollBody}>{children}</div>
      {footer && <div style={S.modalFoot}>{footer}</div>}
    </div>
  </div>
);

export const FormGrid = ({ children, cols = 2 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols},1fr)`, gap: 14, marginBottom: 18 }}>{children}</div>
);

export const useToast = () => {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); }, []);
  const ToastEl = toast ? (
    <div style={{ position: 'fixed', top: 18, right: 18, zIndex: 9999, padding: '11px 18px', borderRadius: 9, background: toast.type === 'error' ? '#fdf2f2' : '#f3faf7', color: toast.type === 'error' ? C.danger : C.success, fontWeight: 600, fontSize: 13, border: `1px solid ${toast.type === 'error' ? '#f8d7d7' : '#bcf0da'}`, boxShadow: '0 6px 20px rgba(0,0,0,.1)', display: 'flex', alignItems: 'center', gap: 8, minWidth: 220 }}>
      {toast.type === 'error' ? '❌' : '✅'} {toast.msg}
    </div>
  ) : null;
  return { show, ToastEl };
};

export const useConfirm = () => {
  const [state, setState] = useState(null);
  const confirm = msg => new Promise(resolve => setState({ msg, resolve }));
  const Dialog = state ? (
    <div style={S.modalOverlay} onClick={() => { state.resolve(false); setState(null); }}>
      <div style={{ ...S.modalBox, maxWidth: 360 }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '28px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 34, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontWeight: 700, fontSize: 15, color: C.text, marginBottom: 8 }}>Confirm Delete</div>
          <div style={{ fontSize: 13, color: C.textSoft, marginBottom: 22 }}>{state.msg}</div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button style={S.btnSec} onClick={() => { state.resolve(false); setState(null); }}>Cancel</button>
            <button style={S.btnDng} onClick={() => { state.resolve(true); setState(null); }}>Delete</button>
          </div>
        </div>
      </div>
    </div>
  ) : null;
  return { confirm, Dialog };
};

// Generic CRUD hook
export const useCRUD = (ctrl, companyId) => {
  const [data, setData]     = useState([]);
  const [total, setTotal]   = useState(0);
  const [page, setPage]     = useState(1);
  const [tp, setTp]         = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const { show, ToastEl }   = useToast();
  const { confirm, Dialog } = useConfirm();

  const fetchAll = useCallback(async (extra = {}) => {
    setLoading(true);
    const res = await ctrl.getAll({ companyId, page, search, ...filters, ...extra });
    if (res.success) { setData(res.data); setTotal(res.total || 0); setTp(res.totalPages || 1); }
    else show(res.message, 'error');
    setLoading(false);
  }, [companyId, page, search, filters]);

  const React = require('react');
  React.useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleDelete = async (id, label = 'this record') => {
    const ok = await confirm(`Delete ${label}? This cannot be undone.`);
    if (!ok) return;
    const res = await ctrl.delete(id);
    if (res.success) { show(res.message); fetchAll(); }
    else show(res.message, 'error');
  };

  return { data, total, page, setPage, totalPages: tp, loading, search, setSearch, filters, setFilters, fetchAll, handleDelete, show, ToastEl, Dialog };
};